import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Emblem } from "./Emblem";
import SocialLinks from "./SocialLinks";
import { NAV_LINKS, CONTACT } from "../data/site";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-md transition-all duration-500 ${
          scrolled ? "border-gold/30 bg-ink/95" : "border-gold/15 bg-ink/85"
        }`}
        data-testid="navbar"
      >
        <div
          className={`mx-auto flex max-w-7xl items-center justify-between px-6 transition-all duration-500 lg:px-10 ${
            scrolled ? "h-16" : "h-20 lg:h-24"
          }`}
        >
          <Link to="/" data-testid="nav-brand-link" aria-label="The Royal Mahaveer Roadlines — Home">
            <Emblem compact />
          </Link>

          <nav className="hidden items-center gap-9 lg:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `group relative py-2 text-[11px] font-semibold uppercase tracking-[0.24em] transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-foreground/60 hover:text-gold-light"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span
                      className={`absolute bottom-0 left-0 h-px bg-gold transition-all duration-300 ${
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <SocialLinks className="hidden lg:flex" testIdPrefix="header-social" />
            <Link to="/quote" className="btn-solid group hidden !px-7 !py-3 lg:inline-flex" data-testid="nav-quote-cta">
              Request a Quote
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
            <button
              className="text-foreground transition-colors hover:text-gold-light lg:hidden"
              onClick={() => setOpen(!open)}
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="nav-mobile-toggle"
            >
              {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.nav
            className={`fixed inset-0 ${scrolled ? "top-16" : "top-20"} z-40 flex flex-col bg-ink px-6 pb-24 pt-10 lg:hidden`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            aria-label="Mobile"
            data-testid="mobile-menu"
          >
            {NAV_LINKS.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, x: -18 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.06 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                <NavLink
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center justify-between border-b border-gold/10 py-5 font-heading text-2xl ${
                      isActive ? "text-gold" : "text-foreground"
                    }`
                  }
                >
                  {l.label}
                  <ArrowUpRight className="h-5 w-5 text-gold" />
                </NavLink>
              </motion.div>
            ))}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
              <Link to="/quote" className="btn-solid mt-10 w-full" data-testid="mobile-nav-quote-cta">
                Request a Quote
              </Link>
            </motion.div>
            <motion.div
              className="mt-auto pt-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <a href={`tel:${CONTACT.phoneRaw}`} data-testid="mobile-nav-call-link" className="block text-sm text-foreground/75 transition-colors hover:text-gold-light">
                {CONTACT.phoneDisplay}
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="mobile-nav-email-link" className="mt-2 block break-words text-sm text-foreground/75 transition-colors hover:text-gold-light">
                {CONTACT.email}
              </a>
              <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.24em] text-gold" data-testid="mobile-menu-hours">
                {CONTACT.hours} — call or WhatsApp anytime
              </p>
              <SocialLinks className="mt-4" testIdPrefix="mobile-social" />
            </motion.div>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
}
